import { Stack, Text } from '@hotjar/hotsauce';
import type { GlobalModalProps } from '@hotjar/webapp/features/GlobalModal/createGlobalModal';
import { useEffectOnceWithDependencies } from '@hotjar/webapp/hooks/useEffectOnceWithDependencies';
import { upsellLogger } from '@hotjar/webapp/domains/revenue/utils';
import {
  DatadogActionType,
  DatadogUpsellType,
} from '@hotjar/webapp/domains/revenue/upsell/constants';
import { ErrorBoundary } from '@hotjar/error-handler';
import { useTrafficCoverage } from '@hotjar/webapp/hooks/useTrafficCoverage';
import { useTranslationWithPrefix } from '@hotjar/translations';
import type { Site } from '@hotjar/site';
import { extractSkuFromPlanId, isBundledPlanId } from '@hotjar/webapp/domains/common/product/utils';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import type {
  SubmitCheckoutParams,
  SubmitCheckoutResult,
} from '@hotjar/webapp/domains/revenue/hooks/useUpsellCheckout';
import { createGlobalUpsellModal } from '@hotjar/webapp/domains/revenue/upsell/createGlobalUpsellModal';

import type { BillingPeriod } from '@hotjar/subscription-management';
import { BillingPeriodSwitch, useAnnualBillingEligibility } from '@hotjar/upsell';
import { ModalStyles as S, TrafficBarStyles as BarStyles } from './styles';

import { useModalTitle } from './hooks/useModalTitle';
import { PlanComparisonColumns } from '../PlanComparisonColumns/PlanComparisonColumns';
import { TrafficBar } from './components/TrafficBar';
import { trafficRecommendationFeatureGateDetails } from '../PlanComparisonModal/constants';
import type { TrafficRecommendationReferrer } from '../PlanComparisonModal/types';
import { useModalLoadedTracking } from '../PlanComparisonModal/hooks/useModalLoadedTracking';
import { MODAL_NAME } from './constants';

interface Props {
  site: Site;
  referrer: TrafficRecommendationReferrer;
  isUpgradeRequest: boolean | undefined;
  recommendedPlanId?: string | null;
  additionalTrackingData?: Record<string, string>;
}

export const TrafficRecommendationModal = ({
  site,
  referrer,
  isUpgradeRequest,
  recommendedPlanId: initialRecommendedPlanId,
  additionalTrackingData,
  billingPeriod,
  billingPeriodChanged,
  onCancel,
  onSubmit,
  setBillingPeriod,
}: Props &
  GlobalModalProps<SubmitCheckoutParams> & {
    billingPeriod: BillingPeriod;
    billingPeriodChanged: boolean;
    setBillingPeriod: (value: BillingPeriod) => unknown;
  }): JSX.Element => {
  const {
    isLoading: isTrafficCoverageLoading,
    coveragePercentage,
    isCoverageMeasureComplete,
    dailyTraffic,
    ccDailyAllowance,
    days,
    isError,
    hasPartialMeasure,
  } = useTrafficCoverage();
  const { isLoading: isModalTitleLoading, title } = useModalTitle({
    isLoading: isTrafficCoverageLoading,
    days,
    coveragePercentage,
    isError,
  });
  const { isEligible: isAnnualEligible } = useAnnualBillingEligibility(site.account);

  const { t } = useTranslationWithPrefix('revenue:traffic-recommendation-modal');

  const isLoading = isTrafficCoverageLoading && isModalTitleLoading;

  const recommendedPlanId =
    initialRecommendedPlanId && isBundledPlanId(initialRecommendedPlanId)
      ? extractSkuFromPlanId(initialRecommendedPlanId, ProductType.Analytics)
      : initialRecommendedPlanId;

  useModalLoadedTracking({
    referrer,
    site,
    modalVersion: 'observe_traffic',
    modalName: MODAL_NAME,
    additionalTrackingData: {
      ...additionalTrackingData,
      shown_traffic_coverage: coveragePercentage,
    },
  });

  useEffectOnceWithDependencies(
    () => {
      upsellLogger?.info('Traffic recommendation modal opened', {
        upsellType: DatadogUpsellType.TrafficRecommendation,
        action: DatadogActionType.Open,
      });
    },
    [isLoading, isError],
    () => !isLoading && !isError,
  );

  const onError = (error: unknown) => {
    if (error && error instanceof Error) {
      upsellLogger?.error('Traffic recommendation modal error', {
        upsellType: DatadogUpsellType.TrafficRecommendation,
        action: DatadogActionType.Failure,
        errorMessage: error.message,
      });
    }
  };

  let copyContext;

  if (isError) {
    copyContext = 'error';
  } else if (days === 0) {
    copyContext = '0-days';
  } else if (coveragePercentage === 100) {
    copyContext = 'full-coverage';
  }

  return (
    <>
      {/**
       * Uncaught errors from child components will be propagated up until this error boundary, where we catch them and
       * stop them from propagating further. We also attach the `onError` handler, which will send an error log to
       * Datadog in order to mark a failure in our SLO.
       *
       * Important: This will only catch uncaught sync errors. It won't catch any errors that are already caught
       * somewhere, or if the error was thrown asynchronously. (e.g. in an async function, a promise rejection, or
       * event handler)
       *
       * To catch async errors, you must implement an error handler in that context.
       */}
      <ErrorBoundary squadSentryTag="upsell" onError={onError}>
        <S.Modal
          isOpen
          contentLabel={title}
          onClose={onCancel}
          data-testid="traffic-recommendation-modal"
        >
          <S.Loader isLoading={isLoading} size="xl">
            <S.ModalHeader title={title} />
            <S.ModalBody>
              <TrafficBar
                site={site}
                coveragePercentage={coveragePercentage}
                isCoverageMeasureComplete={isCoverageMeasureComplete}
                dailyTraffic={dailyTraffic}
                ccDailyAllowance={ccDailyAllowance}
                hasPartialMeasure={hasPartialMeasure}
                hasDivider={!copyContext}
              >
                <BarStyles.TextWrapper>
                  <BarStyles.Title>
                    {t('traffic-bar.title', { context: copyContext })}
                  </BarStyles.Title>
                  <BarStyles.Description>
                    {t('traffic-bar.description', { context: copyContext })}
                  </BarStyles.Description>
                </BarStyles.TextWrapper>
              </TrafficBar>
              <S.PlanComparisonColumnsHeader>
                <S.PlanComparisonColumnsLabel>
                  {t('plan-comparison-columns-label')}
                </S.PlanComparisonColumnsLabel>
                {isAnnualEligible ? (
                  <BillingPeriodSwitch
                    value={billingPeriod}
                    onChange={setBillingPeriod}
                    isLocked={billingPeriodChanged === false && billingPeriod === 'ANNUALLY'}
                  />
                ) : null}
              </S.PlanComparisonColumnsHeader>
              <Stack direction="column" spacing="x2">
                <PlanComparisonColumns
                  site={site}
                  featureGate={trafficRecommendationFeatureGateDetails.Traffic}
                  recommendedPlanId={recommendedPlanId}
                  referrer={referrer}
                  isUpgradeRequest={!!isUpgradeRequest}
                  onCancel={onCancel}
                  onSubmit={
                    onSubmit as (params: SubmitCheckoutParams) => Promise<SubmitCheckoutResult>
                  }
                  onError={onError}
                  modalName={MODAL_NAME}
                  billingPeriod={billingPeriod}
                  billingPeriodChanged={billingPeriodChanged}
                />
              </Stack>
            </S.ModalBody>
            <S.ModalFooter>
              <Text textType="caption" textColor="text_light">
                {t('price-disclaimer')}
              </Text>
            </S.ModalFooter>
          </S.Loader>
        </S.Modal>
      </ErrorBoundary>
    </>
  );
};

export const openTrafficRecommendationModal = createGlobalUpsellModal<
  Props,
  SubmitCheckoutParams,
  Error
>(TrafficRecommendationModal);
